import {
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  type JSX,
} from "solid-js";
import type { ConnectionState } from "@energyplatform/client";
import { t } from "../i18n.ts";
import { session } from "../services.ts";
import { canOperate } from "../model.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "./ErrorBox.tsx";

/** Page header in the prototype's style: eyebrow, title, subtitle and actions on the right. */
export function PageHeader(props: {
  eyebrow?: string;
  title: string;
  sub?: string;
  children?: JSX.Element;
}) {
  return (
    <div class="ph">
      <div>
        <Show when={props.eyebrow}>
          <div class="eyebrow">{props.eyebrow}</div>
        </Show>
        <h1>{props.title}</h1>
        <Show when={props.sub}>
          <div class="sub">{props.sub}</div>
        </Show>
      </div>
      <Show when={props.children}>
        <div class="acts">{props.children}</div>
      </Show>
    </div>
  );
}

export function Tabs<T extends string>(props: {
  tabs: readonly T[];
  value: T;
  onChange: (tab: T) => void;
  label: (tab: T) => string;
}) {
  return (
    <div class="tabs" role="tablist">
      <For each={props.tabs}>
        {(tab) => (
          <button
            type="button"
            role="tab"
            aria-selected={props.value === tab}
            onClick={() => props.onChange(tab)}
          >
            {props.label(tab)}
          </button>
        )}
      </For>
    </div>
  );
}

/** Modal dialog; closes on Escape and on a click outside. */
export function Dialog(props: {
  title: string;
  onClose: () => void;
  children: JSX.Element;
  footer?: JSX.Element;
  wide?: boolean;
}) {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      props.onClose();
    }
  };
  onMount(() => document.addEventListener("keydown", onKey));
  onCleanup(() => document.removeEventListener("keydown", onKey));
  return (
    <div
      class="ovl"
      onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}
    >
      <div
        class="dlg"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        style={props.wide ? { width: "min(820px, 100%)" } : undefined}
      >
        <div class="dlg-h">
          <h2>{props.title}</h2>
          <button
            type="button"
            class="btn ghost sm"
            style={{ "margin-left": "auto" }}
            onClick={() => props.onClose()}
            aria-label={t("common.close")}
          >
            <Icon name="x" />
          </button>
        </div>
        <div class="dlg-b">{props.children}</div>
        <Show when={props.footer}>
          <div class="dlg-f">{props.footer}</div>
        </Show>
      </div>
    </div>
  );
}

/** Asks before an irreversible action; the action's error is shown in the dialog. */
export function ConfirmDialog(props: {
  title: string;
  text: string;
  confirm: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}) {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<unknown>();
  const run = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await props.onConfirm();
      props.onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={props.title}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class={`btn primary ${props.danger ? "danger" : ""}`}
            disabled={busy()}
            onClick={() => void run()}
          >
            {props.confirm}
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>{props.text}</p>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
    </Dialog>
  );
}

/** Asks for a new name; validation stays in the backend, its error is shown in place. */
export function RenameDialog(props: {
  title: string;
  value: string;
  onSave: (name: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [name, setName] = createSignal(props.value);
  const [error, setError] = createSignal<unknown>();
  const save = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(undefined);
    try {
      await props.onSave(name().trim());
      props.onClose();
    } catch (err) {
      setError(err);
    }
  };
  return (
    <Dialog title={props.title} onClose={props.onClose}>
      <form class="stack" onSubmit={(e) => void save(e)}>
        <div class="field">
          <label for="renameInput">{t("common.name")}</label>
          <input
            id="renameInput"
            value={name()}
            maxLength={120}
            required
            autofocus
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
        <div class="row-acts" style={{ "justify-content": "flex-end" }}>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button type="submit" class="btn primary">
            {t("common.save")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

const CONNECTION_CHIP: Record<ConnectionState, string> = {
  authorizing: "acc",
  active: "ok",
  degraded: "warn",
  backoff: "warn",
  paused: "stale",
  authExpired: "crit",
  error: "crit",
};

export function ConnectionStateChip(props: { state: ConnectionState }) {
  return (
    <span class={`chip ${CONNECTION_CHIP[props.state]}`}>
      {t(`connectionState.${props.state}`)}
    </span>
  );
}

/** Whether the signed-in user may change things (admin or operator); the host checks every request anyway. */
export function operator(): boolean {
  const s = session();
  return s.status === "signedIn" && canOperate(s.user.roles);
}

/** Runs an action and keeps its error for display next to the control that started it. */
export function useAction(): [
  (action: () => Promise<unknown>) => Promise<boolean>,
  () => unknown,
  () => boolean,
] {
  const [error, setError] = createSignal<unknown>();
  const [busy, setBusy] = createSignal(false);
  const run = async (action: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(true);
    try {
      await action();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [run, error, busy];
}

export function Skeleton(props: { lines?: number }) {
  return (
    <div class="card-b">
      <For each={Array.from({ length: props.lines ?? 3 })}>
        {() => <div class="sk" />}
      </For>
    </div>
  );
}
